/**
 * One backdrop image the shop can put behind a flat-lay, as the app holds it: the pixels stay a
 * Blob (exactly the file that was imported, as the template store keeps its photos) and the
 * worker decodes them once, on demand, into the scene cache the compositor covers to the photo.
 *
 * `width`/`height` are the DECODED size the worker reported — capped at `PHOTO_MAX_SIDE` like a
 * template photo — so the panel can say how big a scene is without decoding it again.
 */
export interface SceneSource { id: string; name: string; width: number; height: number; bytes: Blob }

/** Where the user's scenes are actually being kept. `'memory'` means this session only — the one
 *  line the backdrop panel shows when it happens. */
export type SceneStoreKind = 'indexeddb' | 'memory';

export interface SceneStore {
  list(): Promise<SceneSource[]>;
  put(scene: SceneSource): Promise<void>;
  remove(id: string): Promise<void>;
  /** Where the rows are going RIGHT NOW: an IndexedDB store that has fallen back reports
   *  `'memory'` from then on. Optimistic before the first call — nothing has tried to open the
   *  database yet. */
  kind(): SceneStoreKind;
}

/** Past this a backdrop is not a backdrop but a photo library: the worker decodes the whole image
 *  and keeps it (plus one photo-sized cover per template it is rendered behind), and the file also
 *  has to fit in IndexedDB beside every template photo. */
export const SCENE_MAX_BYTES = 25 * 1024 * 1024;

/** Said wherever a scene can be imported, so the two cannot drift. */
export const SCENES_SESSION_ONLY_NOTICE = 'Scenes are kept for this session only (browser storage unavailable)';

/** The longest name the panel lays out on one row. */
const NAME_MAX = 60;

/** `photos/Studio Grey.jpg` → `Studio Grey`: the file's own name is what the shop recognises a
 *  backdrop by. Trimmed, held to `NAME_MAX`, and never empty — a file called `.jpg` still needs a
 *  row label. A leading dot is part of the name (`.env` is not an extension), which is why the
 *  extension is only cut at a dot past the first character. */
export function sceneName(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  const stem = (dot > 0 ? base.slice(0, dot) : base).trim();
  return stem ? stem.slice(0, NAME_MAX).trim() : 'Scene';
}

/** In-memory store: the fallback when IndexedDB is unavailable (private windows, blocked storage)
 *  and what the unit tests exercise. Scenes then last for the session only, which is still better
 *  than refusing to import one. */
export function memorySceneStore(): SceneStore {
  const rows = new Map<string, SceneSource>();
  return {
    async list() { return [...rows.values()]; },
    async put(scene) { rows.set(scene.id, scene); },
    async remove(id) { rows.delete(id); },
    kind: () => 'memory',
  };
}

const DB_VERSION = 1;
const STORE = 'scenes';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

/**
 * The user's backdrop images in IndexedDB: object store `scenes`, keyed by the scene's own id,
 * each row `{ id, name, width, height, bytes }` with the image as a Blob (structured clone stores
 * it without a base64 round-trip, which matters — these are multi-megabyte photographs that
 * localStorage would refuse outright).
 *
 * A database of its own rather than a second store inside `eip-templates`: adding an object store
 * to that database would mean a version bump, and a bump another tab is holding open blocks the
 * upgrade — which would cost the shop its TEMPLATES for the session over a feature it may never
 * use. Separate databases fail separately.
 *
 * Every call opens the database and closes it again rather than holding a connection, and a
 * database that refuses to OPEN switches the whole store over to `memorySceneStore` for the rest
 * of the session and reports `kind() === 'memory'` — the same one-way fallback the template store
 * makes, for the same reason: importing a scene then still works, it just does not survive a
 * reload, and the shop is told so rather than losing it silently at the next one. A transaction
 * that fails on a database that DID open is a real error and reaches the caller.
 */
export function indexedDbSceneStore(factory: IDBFactory, name = 'eip-scenes'): SceneStore {
  /** Set once the database has refused to open — one way, for the session. */
  let memory: SceneStore | null = null;
  function open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = factory.open(name, DB_VERSION);
      req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' }); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB could not be opened'));
      req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    });
  }
  /** The open database, or null once the store has fallen back to memory. */
  async function database(): Promise<IDBDatabase | null> {
    if (memory) return null;
    try {
      return await open();
    } catch (err) {
      memory = memorySceneStore();
      console.warn('mockup scenes: browser storage is unavailable; scenes are kept for this session only', err);
      return null;
    }
  }
  async function withStore<T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => Promise<T>, fallback: (store: SceneStore) => Promise<T>): Promise<T> {
    const db = await database();
    if (!db) return fallback(memory!);
    try {
      const tx = db.transaction(STORE, mode);
      // A read is complete once its own request has resolved, so it needs no completion promise —
      // and building one it never awaits leaves an unhandled rejection behind whenever the
      // transaction fails. A WRITE does: `put`/`delete` resolve long before the data is durable.
      if (mode === 'readonly') return await body(tx.objectStore(STORE));
      const done = new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      });
      const out = await body(tx.objectStore(STORE));
      await done;
      return out;
    } finally { db.close(); }
  }
  return {
    async list() {
      return withStore('readonly', async (store) => {
        const rows = await request<SceneSource[]>(store.getAll());
        const out: SceneSource[] = [];
        for (const row of rows) {
          // A row written by an older build (or hand-edited) must not take the whole list down
          // with it: check each, skip what cannot be used.
          if (row && typeof row.id === 'string' && row.id && typeof row.name === 'string'
            && Number.isFinite(row.width) && Number.isFinite(row.height) && row.bytes instanceof Blob) {
            out.push({ id: row.id, name: row.name, width: row.width, height: row.height, bytes: row.bytes });
          } else console.warn(`mockup scenes: stored scene "${(row as { id?: unknown })?.id}" is unusable and was skipped`);
        }
        return out;
      }, (m) => m.list());
    },
    async put(scene) {
      await withStore('readwrite', async (store) => {
        // Field by field rather than the object itself: nothing undefined for the structured
        // clone to choke on, and nothing the panel hung off a scene rides into storage.
        store.put({ id: scene.id, name: scene.name, width: scene.width, height: scene.height, bytes: scene.bytes });
      }, (m) => m.put(scene));
    },
    async remove(id) { await withStore('readwrite', async (store) => { store.delete(id); }, (m) => m.remove(id)); },
    kind: () => (memory ? 'memory' : 'indexeddb'),
  };
}
